import { GENERAL_KNOWLEDGE_LABEL, MNEMONIC_LABEL } from "@studyward/shared";
import { cx } from "@studyward/ui";
import { ArrowUp, Bird, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router";
import { useAskHoot, useHootThread } from "../lib/assist";
import { useSets } from "../lib/queries";
import { useTodayPlan } from "../lib/study";

const CLASS_LABEL: Record<string, string | null> = {
  material: "From your material",
  general_knowledge: GENERAL_KNOWLEDGE_LABEL,
  mnemonic: MNEMONIC_LABEL,
  not_in_material: "Not in your material",
  out_of_scope: null,
};

/**
 * Persistent bottom-right round "Ask Hoot" button with Bird icon and global context popover.
 * Connects across all of the student's ready study sets and highlights their weakest blueprint focus areas.
 */
export function GlobalHoot() {
  const { pathname } = useLocation();
  const sets = useSets();
  const today = useTodayPlan();
  const [open, setOpen] = useState(false);
  const [selectedSetId, setSelectedSetId] = useState<string>("");
  const [question, setQuestion] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const readySets = (sets.data?.items ?? []).filter((s) => s.status === "ready");

  // Detect if the user is currently viewing a specific set (/sets/:setId)
  const routeSetMatch = pathname.match(/^\/sets\/([^/]+)/);
  const routeSetId = routeSetMatch?.[1] ?? "";

  const activeSetId =
    (selectedSetId && readySets.some((s) => s.id === selectedSetId) ? selectedSetId : "") ||
    (routeSetId && readySets.some((s) => s.id === routeSetId) ? routeSetId : "") ||
    (readySets[0]?.id ?? "");

  const thread = useHootThread(open ? activeSetId : "");
  const ask = useAskHoot(activeSetId);

  const messages = thread.data?.messages ?? [];
  const usage = thread.data?.usage;
  const capped = usage ? usage.allowed !== null && usage.used >= usage.allowed : false;

  useEffect(() => {
    if (open) {
      scrollRef.current?.scrollIntoView?.({ block: "nearest" });
    }
  }, [open, messages.length]);

  // Hide the floating widget when already on the full-page Ask Hoot route or active timed exam
  if (pathname.endsWith("/hoot") || pathname.startsWith("/mock-exams/")) {
    return null;
  }

  const focusNames = today.data?.focus?.map((f) => f.name) ?? [];
  const focusSummary = focusNames.length > 0 ? focusNames.slice(0, 2).join(" & ") : "All blueprint categories";

  const sendQuestion = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !activeSetId || capped || ask.isPending) return;
    ask.mutate(
      { content: trimmed, kind: "ask", allow_general_knowledge: true },
      { onSuccess: () => setQuestion("") },
    );
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    sendQuestion(question);
  };

  return (
    <div className="fixed bottom-3.5 right-3.5 z-40 flex flex-col items-end gap-2.5 sm:bottom-6 sm:right-6 sm:gap-3">
      {open && (
        <section
          role="dialog"
          aria-label="Hoot"
          className="glass-popover flex w-[min(22.5rem,calc(100vw-1.5rem))] flex-col gap-3 rounded-card p-3.5 text-text-primary sm:p-4"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <span
                aria-hidden="true"
                className="grid size-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary to-primary-pressed text-text-on-primary shadow-sm"
              >
                <Bird size={18} />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-small font-semibold leading-tight text-text-primary">Hoot</p>
                  <span className="rounded-pill bg-primary-soft px-2 py-0.5 text-caption font-semibold text-text-link">
                    Global Context
                  </span>
                </div>
                <p className="truncate text-caption text-text-muted">
                  {readySets.length > 0
                    ? `${readySets.length} set${readySets.length === 1 ? "" : "s"} · Focus: ${focusSummary}`
                    : "Upload lecture slides to start"}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close Hoot"
              className="grid size-8 place-items-center rounded-full text-text-muted transition-colors hover:bg-surface-subtle hover:text-text-primary"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>

          {readySets.length === 0 ? (
            <div className="flex flex-col gap-2.5 rounded-control bg-surface-background p-3.5 text-small">
              <p className="text-text-secondary">
                Upload your lecture PDFs, slides, or notes so Hoot can answer questions with exact page and slide citations.
              </p>
              <Link
                to="/upload"
                onClick={() => setOpen(false)}
                className="inline-flex min-h-9 pointer-coarse:min-h-11 items-center justify-center rounded-pill bg-primary px-4 text-caption font-semibold text-text-on-primary hover:bg-primary-pressed"
              >
                + Upload material
              </Link>
            </div>
          ) : (
            <>
              {/* Context Scope Selector */}
              <div className="flex items-center gap-2 text-caption">
                <label htmlFor="global-hoot-scope" className="shrink-0 font-medium text-text-secondary">
                  Context:
                </label>
                <select
                  id="global-hoot-scope"
                  value={activeSetId}
                  onChange={(e) => setSelectedSetId(e.target.value)}
                  className="min-h-8 pointer-coarse:min-h-11 min-w-0 flex-1 truncate rounded-lg border border-border bg-surface-card px-2.5 py-1 text-caption font-medium text-text-primary outline-none focus:border-primary"
                >
                  {readySets.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Conversation or Quick Prompts */}
              <div className="max-h-56 overflow-y-auto pr-0.5">
                {messages.length === 0 ? (
                  <div className="flex flex-col gap-1.5">
                    <p className="px-1 text-caption font-medium text-text-muted">Suggested questions:</p>
                    {[
                      "Summarize the high-yield clinical concepts in this set.",
                      focusNames[0]
                        ? `Give me a quick clinical pearl on ${focusNames[0]}.`
                        : "What are the most testable priority nursing actions here?",
                    ].map((prompt) => (
                      <button
                        key={prompt}
                        type="button"
                        disabled={capped || ask.isPending}
                        onClick={() => sendQuestion(prompt)}
                        className="flex w-full items-center justify-between gap-2 rounded-xl bg-surface-background px-3 py-2 text-left text-caption font-medium text-text-primary transition-colors hover:bg-primary-soft disabled:opacity-50"
                      >
                        <span className="line-clamp-1">{prompt}</span>
                        <Sparkles size={13} aria-hidden="true" className="shrink-0 text-text-link" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <ol aria-label="Hoot recent messages" className="flex flex-col gap-2">
                    {messages.slice(-4).map((m) => (
                      <li
                        key={m.id}
                        className={cx(
                          "rounded-2xl px-3 py-2 text-caption leading-snug",
                          m.role === "user"
                            ? " max-w-[85%] self-end bg-text-primary text-surface-card"
                            : "max-w-[92%] self-start bg-surface-background text-text-primary",
                        )}
                      >
                        {m.role !== "user" && m.answer_class && CLASS_LABEL[m.answer_class] && (
                          <p className="mb-0.5 text-caption font-semibold text-text-link">
                            {CLASS_LABEL[m.answer_class]}
                          </p>
                        )}
                        <p className="whitespace-pre-wrap">{m.content}</p>
                      </li>
                    ))}
                    <div ref={scrollRef} />
                  </ol>
                )}
                {ask.isPending && (
                  <p role="status" className="mt-2 px-1 text-caption text-text-muted">
                    Hoot is thinking…
                  </p>
                )}
              </div>

              {/* Input bar */}
              <form onSubmit={handleSubmit} className="flex items-center gap-2 rounded-pill border border-border-strong bg-surface-card py-1 pl-3.5 pr-1.5 shadow-xs focus-within:border-primary">
                <label htmlFor="global-hoot-input" className="sr-only">
                  Ask Hoot
                </label>
                <input
                  id="global-hoot-input"
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={capped ? "Free limit reached on this set" : "Ask anything across your lectures…"}
                  disabled={capped || ask.isPending}
                  maxLength={1000}
                  className="min-h-8 pointer-coarse:min-h-11 min-w-0 flex-1 bg-transparent text-caption text-text-primary outline-none placeholder:text-text-muted disabled:cursor-not-allowed"
                />
                <button
                  type="submit"
                  disabled={!question.trim() || capped || ask.isPending}
                  aria-label="Send question to Hoot"
                  className="grid size-7 shrink-0 place-items-center rounded-full bg-primary text-text-on-primary transition-opacity hover:bg-primary-pressed disabled:opacity-40"
                >
                  <ArrowUp size={15} aria-hidden="true" />
                </button>
              </form>

              {/* Footer link to full chat */}
              <div className="flex items-center justify-between px-1 text-caption text-text-muted">
                <span>
                  {usage && usage.allowed !== null ? `${usage.used}/${usage.allowed} free questions used` : "Cited from your slides"}
                </span>
                <Link
                  to={`/sets/${activeSetId}/hoot`}
                  onClick={() => setOpen(false)}
                  className="font-semibold text-text-link hover:underline"
                >
                  Open full view →
                </Link>
              </div>
            </>
          )}
        </section>
      )}

      {/* Floating Round Button with Bird Icon */}
      <div className="flex items-center gap-2.5">
        {!open && (
          <span
            aria-hidden="true"
            className="hidden rounded-pill bg-text-primary/90 px-3 py-1.5 text-caption font-semibold text-surface-card shadow-sm backdrop-blur-md sm:inline-block"
          >
            Ask Hoot
          </span>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle Hoot"
          aria-expanded={open}
          title="Ask Hoot (Global Context)"
          className="relative grid size-12 place-items-center rounded-full border border-white/80 bg-gradient-to-br from-primary to-primary-pressed text-text-on-primary shadow-[0_14px_30px_-6px_rgba(0,113,227,0.48),0_4px_10px_rgba(0,0,0,0.12)] transition-transform hover:-translate-y-0.5 active:translate-y-0 sm:size-14"
        >
          <span
            aria-hidden="true"
            className="absolute right-0.5 top-0.5 size-2.5 rounded-full border-2 border-surface-card bg-success sm:size-3"
          />
          <Bird size={22} aria-hidden="true" className="sm:size-6" />
        </button>
      </div>
    </div>
  );
}
